import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Alert, Skeleton, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Select, InputLabel, FormControl, Autocomplete, Checkbox,
  FormControlLabel, InputAdornment,
} from '@mui/material';
import {
  Send, Refresh, Cancel, ContentCopy, Visibility, VerifiedUser, Download, Search,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { useAppData } from '../context/AppDataContext';
import { invoicesAPI, usersAPI, gymUpiAPI } from '../services/api';
import { CURRENCY_CODE, formatCurrency } from '../utils/currency';
import { createPdf, pdfLine, pdfRect, pdfText, wrapPdfText } from '../utils/pdf';
import ConfirmDialog from '../components/ConfirmDialog';
const STATUS_CONFIG = {
  draft:   { label: 'Draft', color: 'text.secondary', bg: '#F3F4F6', border: '#D1D5DB' },
  sent:    { label: 'Sent', color: '#1D4ED8', bg: '#DBEAFE', border: '#BFDBFE' },
  paid:    { label: 'Paid', color: '#16A34A', bg: '#DCFCE7', border: '#BBF7D0' },
  overdue: { label: 'Overdue', color: '#B91C1C', bg: '#FEE2E2', border: '#FECACA' },
  cancelled: { label: 'Cancelled', color: 'text.primary', bg: '#E5E7EB', border: '#D1D5DB' },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.sent;
  return (
    <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 700, fontSize: 11 }} />
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAmount(value) {
  return formatCurrency(value);
}

function invoiceStatusLabel(status) {
  return STATUS_CONFIG[status]?.label || status || 'Sent';
}

function moneyForPdf(value) {
  return `${CURRENCY_CODE} ${Number(value || 0).toLocaleString('en-IN')}`;
}

function buildMemberInvoicePdf(filename, invoice, gym) {
  const invoiceId = String(invoice._id || '').slice(-8).toUpperCase() || Date.now();
  const isPaid = invoice.status === 'paid';
  const docType = isPaid ? 'BILL' : 'INVOICE';
  const docLabel = isPaid ? 'Bill' : 'Invoice';
  const memberName = invoice.userId?.name || 'Member';
  const memberEmail = invoice.userId?.email || '-';
  const gymName = invoice.gymId?.name || gym?.name || 'Gym';
  const invoiceTitle = invoice.title || 'Membership Fee';
  const amount = Number(invoice.amount || 0);
  const paymentAmount = Number(invoice.paidVia?.amount || amount || 0);
  const paidAmount = isPaid ? paymentAmount : 0;
  const balance = Math.max(amount - paidAmount, 0);
  const membership = invoice.membershipType
    ? `${invoice.membershipType.charAt(0).toUpperCase()}${invoice.membershipType.slice(1)}${invoice.shiftToNextMonth ? ' (next month start)' : ''}`
    : '-';
  const notes = isPaid
    ? 'Payment received successfully. This bill confirms settlement of the listed membership charges.'
    : invoice.description || 'Please complete the payment on or before the due date.';
  const upiId = invoice.upiSettingsSnapshot?.upiId || '-';
  const utr = invoice.paidVia?.utrNumber || '-';
  const paymentMode = invoice.paidVia ? 'UPI' : '-';
  const paymentStatus = invoice.paidVia?.status
    ? invoice.paidVia.status.charAt(0).toUpperCase() + invoice.paidVia.status.slice(1)
    : isPaid ? 'Verified' : '-';
  const paymentDate = fmtDate(invoice.paidAt || invoice.paidVia?.verifiedAt || invoice.paidVia?.createdAt);
  const paymentNote = invoice.paidVia?.note || '';
  const footerNote = paymentNote || notes;

  createPdf(filename, () => {
    const c = [];
    const pageW = 595;
    const left = 48;
    const right = 547;
    const tableTop = 505;
    const rowH = 34;

    c.push(pdfText(docType, pageW / 2, 796, { size: 24, bold: true, align: 'center' }));
    c.push(pdfText(`Fitistic Member ${docLabel}`, pageW / 2, 776, { size: 11, align: 'center', color: '0.34 0.40 0.48' }));
    c.push(pdfLine(left, 756, right, 756, '0.10 0.31 0.65', 1.5));

    c.push(pdfText('Bill From', left, 728, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
    c.push(pdfText(gymName, left, 710, { size: 13, bold: true }));
    c.push(pdfText('Fitistic Gym Billing', left, 694, { size: 10, color: '0.34 0.40 0.48' }));
    c.push(pdfText(`UPI ID: ${upiId}`, left, 678, { size: 10, color: '0.34 0.40 0.48' }));

    c.push(pdfText(`${docLabel} No.`, 365, 728, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
    c.push(pdfText(invoiceId, right, 728, { size: 10, bold: true, align: 'right' }));
    c.push(pdfText(`${docLabel} Date`, 365, 710, { size: 9, color: '0.34 0.40 0.48' }));
    c.push(pdfText(fmtDate(invoice.sentAt || invoice.createdAt), right, 710, { size: 10, align: 'right' }));
    c.push(pdfText(isPaid ? 'Payment Date' : 'Due Date', 365, 692, { size: 9, color: '0.34 0.40 0.48' }));
    c.push(pdfText(isPaid ? paymentDate : fmtDate(invoice.dueDate), right, 692, { size: 10, align: 'right' }));
    c.push(pdfText('Status', 365, 674, { size: 9, color: '0.34 0.40 0.48' }));
    c.push(pdfText(invoiceStatusLabel(invoice.status), right, 674, { size: 10, bold: true, align: 'right' }));

    c.push(pdfRect(left, 602, 499, 52, { stroke: '0.85 0.87 0.91', fill: '0.97 0.98 0.99' }));
    c.push(pdfText('Bill To', left + 14, 633, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
    c.push(pdfText(memberName, left + 14, 616, { size: 12, bold: true }));
    c.push(pdfText(memberEmail, 310, 616, { size: 10, color: '0.34 0.40 0.48' }));

    c.push(pdfText('Payment Details', left, 564, { size: 11, bold: true }));
    c.push(pdfText(`Membership: ${membership}`, left, 546, { size: 10, color: '0.34 0.40 0.48' }));
    c.push(pdfText(`Payment Mode: ${paymentMode}`, 365, 546, { size: 10, color: '0.34 0.40 0.48' }));
    if (isPaid) {
      c.push(pdfText(`UTR: ${utr}`, left, 528, { size: 10, color: '0.34 0.40 0.48' }));
      c.push(pdfText(`Payment Status: ${paymentStatus}`, 365, 528, { size: 10, color: '0.34 0.40 0.48' }));
    }

    c.push(pdfRect(left, tableTop - rowH, 499, rowH, { stroke: '0.85 0.87 0.91', fill: '0.95 0.97 1' }));
    c.push(pdfText('#', left + 12, tableTop - 22, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
    c.push(pdfText('Particulars', left + 42, tableTop - 22, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
    c.push(pdfText('Qty', 360, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));
    c.push(pdfText('Rate', 446, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));
    c.push(pdfText('Amount', 532, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));

    c.push(pdfRect(left, tableTop - rowH * 2, 499, rowH, { stroke: '0.85 0.87 0.91' }));
    c.push(pdfText('1', left + 12, tableTop - 56, { size: 10 }));
    c.push(pdfText(invoiceTitle, left + 42, tableTop - 52, { size: 10, bold: true }));
    c.push(pdfText(membership, left + 42, tableTop - 66, { size: 8, color: '0.34 0.40 0.48' }));
    c.push(pdfText('1', 360, tableTop - 56, { size: 10, align: 'right' }));
    c.push(pdfText(moneyForPdf(amount), 446, tableTop - 56, { size: 10, align: 'right' }));
    c.push(pdfText(moneyForPdf(amount), 532, tableTop - 56, { size: 10, align: 'right' }));

    c.push(pdfLine(78, tableTop - rowH * 2, 78, tableTop, '0.85 0.87 0.91'));
    c.push(pdfLine(372, tableTop - rowH * 2, 372, tableTop, '0.85 0.87 0.91'));
    c.push(pdfLine(458, tableTop - rowH * 2, 458, tableTop, '0.85 0.87 0.91'));

    const totalsX = 345;
    const totalsY = 374;
    c.push(pdfText('Subtotal', totalsX, totalsY, { size: 10, color: '0.34 0.40 0.48' }));
    c.push(pdfText(moneyForPdf(amount), right, totalsY, { size: 10, align: 'right' }));
    c.push(pdfText('Amount Paid', totalsX, totalsY - 20, { size: 10, color: '0.34 0.40 0.48' }));
    c.push(pdfText(moneyForPdf(paidAmount), right, totalsY - 20, { size: 10, align: 'right' }));
    if (isPaid) {
      c.push(pdfText('Payment Method', totalsX, totalsY - 40, { size: 10, color: '0.34 0.40 0.48' }));
      c.push(pdfText(paymentMode, right, totalsY - 40, { size: 10, align: 'right' }));
      c.push(pdfLine(totalsX, totalsY - 52, right, totalsY - 52));
      c.push(pdfText('Balance Due', totalsX, totalsY - 74, { size: 12, bold: true }));
      c.push(pdfText(moneyForPdf(balance), right, totalsY - 74, { size: 12, bold: true, align: 'right' }));
    } else {
    c.push(pdfLine(totalsX, totalsY - 32, right, totalsY - 32));
    c.push(pdfText('Balance Due', totalsX, totalsY - 54, { size: 12, bold: true }));
    c.push(pdfText(moneyForPdf(balance), right, totalsY - 54, { size: 12, bold: true, align: 'right' }));
    }

    const noteMaxWidth = totalsX - left - 24;
    const noteLines = wrapPdfText(footerNote, noteMaxWidth, 10).slice(0, 4);
    c.push(pdfText(isPaid ? 'Payment Note' : 'Notes', left, 318, { size: 11, bold: true }));
    noteLines.forEach((line, index) => {
      c.push(pdfText(line, left, 300 - index * 16, { size: 10, color: '0.34 0.40 0.48' }));
    });
    c.push(pdfLine(left, 92, right, 92));
    c.push(pdfText(isPaid ? 'Thank you. Payment received.' : 'Thank you for choosing Fitistic.', pageW / 2, 70, { size: 10, align: 'center', color: '0.34 0.40 0.48' }));
    return c;
  });
}

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function calculateDefaultDueDate(gym) {
  const demoDays = Number.isFinite(Number(gym?.trialClassDays)) ? Number(gym.trialClassDays) : 3;
  const nextDueDate = new Date();
  nextDueDate.setDate(nextDueDate.getDate() + demoDays);
  return formatDateInput(nextDueDate);
}

export default function Invoices() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const gymId = currentUser?.gymId?._id || currentUser?.gymId;
  const { gyms } = useAppData();

  const [stats, setStats] = useState({
    sent: 0, paid: 0, overdue: 0, cancelled: 0, total: 0,
    collected: 0, outstanding: 0, pendingVerification: 0,
  });
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState('Membership Fee');
  const [description, setDescription] = useState('Please complete the membership payment on time.');
  const [amount, setAmount] = useState('');
  const [membershipType, setMembershipType] = useState('');
  const [shiftToNextMonth, setShiftToNextMonth] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState({ open: false, invoice: null });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // UTR verify/reject dialog
  const [utrDialog, setUtrDialog] = useState({ open: false, payment: null, invoice: null });
  const [rejectReason, setRejectReason] = useState('');
  const [utrActionLoading, setUtrActionLoading] = useState(false);

  const currentGym = gyms.find((gym) => gym._id === gymId);
  const membershipTypes = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' },
  ];

  const calculateMembershipAmount = (type, shift) => {
    if (!currentGym || !type) return '';
    const pricing = currentGym.pricing?.[type];
    if (!pricing || !pricing.price) return '';
    const now = new Date();
    const planPrice = Number(pricing.price);
    if (!shift) return planPrice.toFixed(2);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const end = new Date(nextMonth);
    end.setMonth(end.getMonth() + (type === 'annual' ? 12 : type === 'quarterly' ? 3 : 1));
    const durationDays = Math.round((end - nextMonth) / (1000 * 60 * 60 * 24));
    const dailyRate = planPrice / (durationDays || 30);
    const remainingDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate() + 1;
    return Number(planPrice + dailyRate * remainingDays).toFixed(2);
  };

  useEffect(() => {
    if (membershipType) {
      const computed = calculateMembershipAmount(membershipType, shiftToNextMonth);
      if (computed) {
        setAmount(computed);
      }
    }
  }, [membershipType, shiftToNextMonth, currentGym]);

  useEffect(() => {
    if (openDialog && !dueDate) {
      setDueDate(calculateDefaultDueDate(currentGym));
    }
  }, [openDialog, currentGym, dueDate]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersAPI.getAll({ gymId, role: 'user', limit: 200 });
      setUsers(res.data.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [gymId]);

  const loadStats = useCallback(async () => {
    try {
      const res = await invoicesAPI.getStats();
      setStats(res.data.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10, status: statusFilter || undefined, search: search || undefined };
      const res = await invoicesAPI.getAdminList(params);
      setInvoices(res.data.data || []);
      setPages(res.data.pages || 1);
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to load invoices' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { loadUsers(); loadStats(); }, [loadUsers, loadStats]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const sendInvoice = async () => {
    if (selectedUsers.length === 0) {
      setAlert({ type: 'error', msg: 'Select at least one user' });
      return;
    }
    if (!amount || Number(amount) <= 0 || !dueDate) {
      setAlert({ type: 'error', msg: 'Amount and due date are required' });
      return;
    }

    try {
      const payload = {
        userIds: selectedUsers.map(u => u._id),
        title,
        description,
        amount: Number(amount),
        dueDate,
        membershipType: membershipType || undefined,
        shiftToNextMonth: shiftToNextMonth || undefined,
      };
      await invoicesAPI.sendBulk(payload);
      setAlert({ type: 'success', msg: 'Invoices sent successfully' });
      setOpenDialog(false);
      setSelectedUsers([]);
      setAmount('');
      setDueDate('');
      loadStats();
      loadInvoices();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to send invoices' });
    }
  };

  const openDetails = async (invoice) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await invoicesAPI.getOne(invoice._id);
      setDetailInvoice(res.data.data);
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to load invoice details' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openUtrDialog = async (invoice) => {
    setDetailLoading(true);
    try {
      const res = await invoicesAPI.getOne(invoice._id);
      const full = res.data.data;
      setUtrDialog({ open: true, payment: full.paidVia, invoice: full });
      setRejectReason('');
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to load payment details' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleVerifyUtr = async () => {
    if (!utrDialog.payment) return;
    setUtrActionLoading(true);
    try {
      await gymUpiAPI.verifyPayment(utrDialog.payment._id);
      setAlert({ type: 'success', msg: 'Payment verified — invoice marked as paid' });
      setUtrDialog({ open: false, payment: null, invoice: null });
      loadStats();
      loadInvoices();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Verification failed' });
    } finally {
      setUtrActionLoading(false);
    }
  };

  const handleRejectUtr = async () => {
    if (!utrDialog.payment) return;
    setUtrActionLoading(true);
    try {
      await gymUpiAPI.rejectPayment(utrDialog.payment._id, rejectReason);
      setAlert({ type: 'warning', msg: 'Payment rejected — user can resubmit UTR' });
      setUtrDialog({ open: false, payment: null, invoice: null });
      loadStats();
      loadInvoices();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Rejection failed' });
    } finally {
      setUtrActionLoading(false);
    }
  };

  const cancelInvoice = (invoice) => {
    setConfirmCancel({ open: true, invoice });
  };

  const doCancelInvoice = async () => {
    const invoice = confirmCancel.invoice;
    setConfirmCancel({ open: false, invoice: null });
    try {
      await invoicesAPI.cancel(invoice._id);
      setAlert({ type: 'success', msg: 'Invoice cancelled' });
      loadStats();
      loadInvoices();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Cancel failed' });
    }
  };

  const downloadInvoicePdf = (invoice) => {
    const invoiceId = String(invoice._id || '').slice(-8).toUpperCase() || Date.now();
    const prefix = invoice.status === 'paid' ? 'member-bill' : 'member-invoice';
    buildMemberInvoicePdf(`${prefix}-${invoiceId}.pdf`, invoice, currentGym);
  };

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'sent', label: 'Sent' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Invoices"
        subtitle="Send membership invoices and track payment requests."
        gradient="linear-gradient(90deg, #16A34A, #4ADE80)"
        actions={<Button size="small" startIcon={<Send />} variant="contained" onClick={() => setOpenDialog(true)}>Send Invoice</Button>}
      />

      {/* ── Financial Summary ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
        {/* Collected — primary metric */}
        <Paper sx={{ gridColumn: { xs: 'span 2', sm: 'span 3', md: 'span 2' }, p: 2.5, border: '1px solid #BBF7D0', bgcolor: '#F0FDF4' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
            💰 Total Collected
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#16A34A', mt: 0.5, letterSpacing: '-0.03em' }}>
            {formatCurrency(stats.collected)}
          </Typography>
          <Typography variant="caption" color="text.secondary">{stats.paid} invoice{stats.paid !== 1 ? 's' : ''} paid</Typography>
        </Paper>

        {/* Outstanding */}
        <Paper sx={{ p: 2, border: '1px solid #FDE68A', bgcolor: '#FFFBEB' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
            Outstanding
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#D97706', mt: 0.5 }}>
            {formatCurrency(stats.outstanding)}
          </Typography>
          <Typography variant="caption" color="text.secondary">{stats.sent + stats.overdue} pending</Typography>
        </Paper>

        {/* Pending UTR verification */}
        <Paper sx={{ p: 2, border: stats.pendingVerification > 0 ? '1px solid #FCA5A5' : '1px solid #E5E7EB', bgcolor: stats.pendingVerification > 0 ? '#FEF2F2' : '#F9FAFB' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
            Verify UTR
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: stats.pendingVerification > 0 ? '#DC2626' : '#6B7280', mt: 0.5 }}>
            {stats.pendingVerification}
          </Typography>
          <Typography variant="caption" color="text.secondary">awaiting review</Typography>
        </Paper>

        {/* Overdue */}
        <Paper sx={{ p: 2, border: '1px solid #FECACA', bgcolor: '#FFF1F2' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
            Overdue
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#B91C1C', mt: 0.5 }}>
            {stats.overdue}
          </Typography>
          <Typography variant="caption" color="text.secondary">past due date</Typography>
        </Paper>

        {/* Total sent */}
        <Paper sx={{ p: 2, border: '1px solid #BFDBFE', bgcolor: '#EFF6FF' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
            Total Sent
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#1D4ED8', mt: 0.5 }}>
            {stats.total}
          </Typography>
          <Typography variant="caption" color="text.secondary">all time</Typography>
        </Paper>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          placeholder="Search by name or invoice title…"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
          }}
        />
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          sx={{ minWidth: 160 }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => selected === '' ? 'All Statuses' : statusOptions.find(o => o.value === selected)?.label || selected,
          }}
        >
          {statusOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Button startIcon={<Refresh />} size="small" variant="outlined" onClick={() => { loadStats(); loadInvoices(); }}>Refresh</Button>
      </Stack>

      {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}

      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? [...Array(8)].map((_, idx) => (
              <TableRow key={idx}>
                <TableCell><Skeleton width={120} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={90} /></TableCell>
                <TableCell><Skeleton width={90} /></TableCell>
                <TableCell><Skeleton width={110} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
              </TableRow>
            )) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={7}><Box sx={{ py: 6, textAlign: 'center' }}>No invoices found.</Box></TableCell></TableRow>
            ) : invoices.map((invoice) => (
              <TableRow key={invoice._id}>
                <TableCell>{invoice.userId?.name || 'User'}</TableCell>
                <TableCell>{invoice.title}</TableCell>
                <TableCell>{fmtAmount(invoice.amount)}</TableCell>
                <TableCell>{fmtDate(invoice.dueDate)}</TableCell>
                <TableCell><StatusChip status={invoice.status} /></TableCell>
                <TableCell>{fmtDate(invoice.sentAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="View details"><IconButton size="small" onClick={() => openDetails(invoice)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download PDF">
                      <IconButton size="small" onClick={() => downloadInvoicePdf(invoice)}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Show Verify UTR button when payment is submitted but not yet verified */}
                    {invoice.paidVia && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                      <Tooltip title="Verify UTR">
                        <IconButton size="small" color="warning" onClick={() => openUtrDialog(invoice)}>
                          <VerifiedUser fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                      <Tooltip title="Cancel invoice">
                        <IconButton size="small" color="error" onClick={() => cancelInvoice(invoice)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Pagination count={pages} page={page} onChange={(_, value) => setPage(value)} color="primary" />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(option) => option.name || option.email || 'User'}
              value={selectedUsers}
              onChange={(_, value) => setSelectedUsers(value)}
              renderInput={(params) => <TextField {...params} label="Select users" placeholder="Choose members" />}
            />
            <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Membership type</InputLabel>
              <Select
                value={membershipType}
                label="Membership type"
                size="small"
                onChange={e => setMembershipType(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {membershipTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {membershipType && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shiftToNextMonth}
                    onChange={(e) => setShiftToNextMonth(e.target.checked)}
                  />
                }
                label="Shift start to next month and prorate remaining days"
              />
            )}
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              fullWidth
              disabled={Boolean(membershipType)}
              helperText={membershipType ? 'Amount is calculated from gym pricing configuration' : ''}
            />
            <TextField
              label="Due date"
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={currentGym ? `Defaults to ${Number.isFinite(Number(currentGym?.trialClassDays)) ? Number(currentGym.trialClassDays) : 3} demo class days when left blank` : 'Defaults to demo class days when left blank'}
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendInvoice}>Send</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invoice details</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ py: 4 }}><Skeleton height={24} /><Skeleton height={24} sx={{ mt: 1 }} /><Skeleton height={24} sx={{ mt: 1 }} /></Box>
          ) : detailInvoice ? (
            <Stack spacing={2} mt={1}>
              <Typography><strong>User:</strong> {detailInvoice.userId?.name || detailInvoice.userId?.email}</Typography>
              <Typography><strong>Title:</strong> {detailInvoice.title}</Typography>
              <Typography><strong>Amount:</strong> {fmtAmount(detailInvoice.amount)}</Typography>
              <Typography><strong>Due date:</strong> {fmtDate(detailInvoice.dueDate)}</Typography>
              <Typography><strong>Status:</strong> <StatusChip status={detailInvoice.status} /></Typography>
              <Typography><strong>Description:</strong> {detailInvoice.description || '—'}</Typography>
              {detailInvoice.membershipType && (
                <Typography><strong>Membership:</strong> {detailInvoice.membershipType.charAt(0).toUpperCase() + detailInvoice.membershipType.slice(1)}{detailInvoice.shiftToNextMonth ? ' (next month start)' : ''}</Typography>
              )}
              <Divider />
              <Typography variant="subtitle2">UPI Snapshot</Typography>
              <Typography><strong>UPI ID:</strong> {detailInvoice.upiSettingsSnapshot?.upiId || '—'}</Typography>
              {detailInvoice.upiSettingsSnapshot?.qrImage ? (
                <Box component="img" src={detailInvoice.upiSettingsSnapshot.qrImage} alt="QR" sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
              ) : null}
              {detailInvoice.paidVia ? (
                <Box>
                  <Typography variant="subtitle2">Payment details</Typography>
                  <Typography><strong>UTR:</strong> {detailInvoice.paidVia.utrNumber}</Typography>
                  <Typography><strong>Status:</strong> {detailInvoice.paidVia.status}</Typography>
                  <Typography><strong>Note:</strong> {detailInvoice.paidVia.note || '—'}</Typography>
                  {detailInvoice.paidVia.screenshotUrl ? (
                    <Box component="img" src={detailInvoice.paidVia.screenshotUrl} alt="Screenshot" sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          ) : (
            <Typography>Invoice details unavailable.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {detailInvoice?.paidVia && detailInvoice.status !== 'paid' && detailInvoice.status !== 'cancelled' && (
            <Button variant="contained" color="success" onClick={() => { setDetailOpen(false); openUtrDialog(detailInvoice); }}>
              Verify UTR
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* UTR Verify / Reject Dialog */}
      <Dialog open={utrDialog.open} onClose={() => !utrActionLoading && setUtrDialog({ open: false, payment: null, invoice: null })} fullWidth maxWidth="sm">
        <DialogTitle>Verify Payment UTR</DialogTitle>
        <DialogContent>
          {utrDialog.invoice && utrDialog.payment && (
            <Stack spacing={2} mt={1}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Check your UPI bank statement for UTR <strong>{utrDialog.payment.utrNumber}</strong> and confirm the amount matches before approving.
              </Alert>
              <Typography><strong>User:</strong> {utrDialog.invoice.userId?.name} ({utrDialog.invoice.userId?.email})</Typography>
              <Typography><strong>Invoice:</strong> {utrDialog.invoice.title}</Typography>
              <Typography><strong>Amount:</strong> {fmtAmount(utrDialog.invoice.amount)}</Typography>
              <Divider />
              <Typography variant="subtitle2">Submitted Payment</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography><strong>UTR Number:</strong> {utrDialog.payment.utrNumber}</Typography>
                <Tooltip title="Copy UTR">
                  <IconButton size="small" onClick={() => navigator.clipboard.writeText(utrDialog.payment.utrNumber)}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              {utrDialog.payment.note && <Typography><strong>Note:</strong> {utrDialog.payment.note}</Typography>}
              {utrDialog.payment.screenshotUrl && (
                <Box component="img" src={utrDialog.payment.screenshotUrl} alt="Payment screenshot"
                  sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
              )}
              <Divider />
              <TextField
                label="Rejection reason (required only if rejecting)"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. UTR not found in bank statement"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setUtrDialog({ open: false, payment: null, invoice: null })} disabled={utrActionLoading}>
            Cancel
          </Button>
          <Button variant="outlined" color="error" onClick={handleRejectUtr} disabled={utrActionLoading}>
            {utrActionLoading ? 'Processing…' : 'Reject UTR'}
          </Button>
          <Button variant="contained" color="success" onClick={handleVerifyUtr} disabled={utrActionLoading}>
            {utrActionLoading ? 'Processing…' : '✓ Approve & Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmCancel.open}
        title="Cancel Invoice"
        message={`Cancel invoice "${confirmCancel.invoice?.title}"? This action cannot be undone.`}
        confirmLabel="Cancel Invoice"
        confirmColor="warning"
        onConfirm={doCancelInvoice}
        onCancel={() => setConfirmCancel({ open: false, invoice: null })}
      />
    </Box>
  );
}
